/**
 * Rate-limited logging utility
 * Prevents console spam by limiting same log key to once per cooldown period
 * 
 * Features:
 * - rateLog: Logs max once per 15s per key
 * - rateOnce: Logs only once per page load per key
 * - Supports all console levels (log, warn, error, debug)
 */

interface LogEntry {
  timestamp: number;
  count: number; // How many times this key was logged (for reference)
  logged: boolean; // For rateOnce - whether it was already logged
}

const logCache = new Map<string, LogEntry>();
const DEFAULT_COOLDOWN_MS = 15000; // 15 seconds

/**
 * Rate-limited logging - logs max once per cooldown period per key
 * @param key Unique identifier for this log message (e.g., 'reminder-fetch-error')
 * @param level Console level: 'log' | 'warn' | 'error' | 'debug'
 * @param args Arguments to pass to console method
 * @param cooldownMs Cooldown period in milliseconds (default: 15s)
 */
export function rateLog(
  key: string,
  level: 'log' | 'warn' | 'error' | 'debug' = 'log',
  cooldownMs: number = DEFAULT_COOLDOWN_MS,
  ...args: any[]
): void {
  const now = Date.now();
  const entry = logCache.get(key);
  
  if (!entry || (now - entry.timestamp) >= cooldownMs) {
    const consoleMethod = console[level] || console.log;
    consoleMethod(...args);
    
    logCache.set(key, {
      timestamp: now,
      count: (entry?.count || 0) + 1,
      logged: true,
    });
  }
}

/**
 * Log only once per page load - never logs again for same key
 * @param key Unique identifier for this log message
 * @param level Console level: 'log' | 'warn' | 'error' | 'debug'
 * @param args Arguments to pass to console method
 */
export function rateOnce(
  key: string,
  level: 'log' | 'warn' | 'error' | 'debug' = 'log',
  ...args: any[]
): void {
  const entry = logCache.get(key);
  
  if (!entry || !entry.logged) {
    const consoleMethod = console[level] || console.log;
    consoleMethod(...args);
    
    logCache.set(key, {
      timestamp: Date.now(),
      count: 1,
      logged: true,
    });
  }
}

/**
 * Clear log cache (useful for testing or manual cleanup)
 */
export function clearRateLogCache(): void {
  logCache.clear();
}

/**
 * Get log statistics for a key (useful for debugging)
 */
export function getRateLogStats(key: string): { count: number; lastLogged: number | null } | null {
  const entry = logCache.get(key);
  if (!entry) return null;
  
  return {
    count: entry.count,
    lastLogged: entry.timestamp,
  };
}

